import { PlansColumns, PlansComparisonSection } from '~/features/plans/models/plans-comparison';

// Columns match the plans page's plan tiers: Individual, Team, Enterprise.
export const PLANS_COMPARISON_COLUMNS: PlansColumns = [
  { id: 'individual', title: 'Individual' },
  { id: 'team', title: 'Team' },
  { id: 'enterprise', title: 'Enterprise' },
];

// Content hardcoded from the live Sanity `plansMatrix` field on the /plans page.
// Section titles mirror the labels the pre-redesign PlansMatrix rendered for each key.
// Keep values verbatim (including the `[Contact us](...)` markdown link) — do not paraphrase.
export const PLANS_COMPARISON_SECTIONS: PlansComparisonSection[] = [
  {
    title: 'Best in class content',
    rows: [
      {
        title: 'Full access to hundreds of Nanodegrees and courses',
        description:
          "Access Udacity's full range of Nanodegrees and courses built in partnership with industry experts to develop practitioner-level expertise.",
        plans: { individual: true, team: true, enterprise: true },
      },
      {
        title: 'Full access to all Discovery and Fluency programs',
        description:
          "Access Udacity's entire Discovery and Fluency library built to provide high-level knowledge via shorter learning experiences.",
        plans: { individual: true, team: true, enterprise: true },
      },
      {
        title: 'Full access to all hands-on, real-world projects',
        description:
          'Gain hands-on experience with projects crafted by expert practitioners and based on real industry scenarios.',
        plans: { individual: true, team: true, enterprise: true },
      },
      {
        title: 'Udacity-verified certification',
        description: 'Earn Udacity-verified certification upon program completion',
        plans: { individual: true, team: true, enterprise: true },
      },
    ],
  },
  {
    title: 'Learner Experience',
    rows: [
      {
        title: 'Human-powered project feedback and code reviews',
        description:
          'Complete real-world projects with expert feedback to enhance practical skills and industry knowledge.',
        plans: { individual: true, team: true, enterprise: true },
      },
      {
        title: 'Weekly learning progress updates',
        description: 'Track learning progress with regular emails summarizing program completion.',
        plans: { individual: true, team: true, enterprise: true },
      },
      {
        title: 'Access to AI-powered learning assistant 24/7',
        description: 'Chat with our AI-powered learning assistant for swift, personalized learning support.',
        plans: { individual: true, team: true, enterprise: true },
      },
      {
        title: 'Access to learner community',
        description: 'Engage with a diverse professional community for collaborative learning.',
        plans: { individual: true, team: true, enterprise: true },
      },
      {
        title: 'Learner leaderboard',
        description: 'Compare learning progress with our interactive leaderboard.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'Cohort learning model',
        description: 'Learn alongside peers and reach program milestones together.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'Priority technical support',
        description: 'Receive expedited assistance from our support team for critical issues.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'Program accelerators',
        description:
          "Access exclusive, personalized 1:1 sessions on industry trends and technical domains led by Udacity mentors to advance your organization's learning.",
        plans: { individual: false, team: false, enterprise: true },
      },
    ],
  },
  {
    title: 'Udacity Management Platform',
    rows: [
      {
        title: 'Skill attainment dashboard',
        description: 'Track analytics for skills learned, skill proficiency, and time to mastery.',
        plans: { individual: false, team: true, enterprise: true },
      },
      {
        title: 'Learner assessments',
        description:
          'Learner assessments to establish skills baseline, determine readiness for programs, and measure learning outcomes.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'Learning paths',
        description:
          'Optimize employee skill development with tailored training routes aligned to organizational objectives.',
        plans: { individual: false, team: false, enterprise: true },
      },
    ],
  },
  {
    title: 'Program Manager Tools',
    rows: [
      {
        title: 'Manage and send invitations',
        description: 'Streamline user onboarding with bulk actions to manage invitations.',
        plans: { individual: false, team: true, enterprise: true },
      },
      {
        title: 'Dashboard with aggregate learner stats and reporting',
        description:
          'View and analyze progress of learners across milestones with the ability to send in-product notifications to select learners.',
        plans: { individual: false, team: true, enterprise: true },
      },
    ],
  },
  {
    title: 'Solutions Consulting Services',
    rows: [
      {
        title: 'Industry skill insights and benchmarking',
        description: 'Gain insights and benchmark team skill attainment against industry standards.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'Skills and competency mapping',
        description:
          'Develop a competency map for your organization to better understand where skill concentrations exist.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'Custom learning paths',
        description:
          'Create tailored learning journeys to align with specific goals and skill requirements and that tie to business objectives.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'Custom projects',
        description:
          "Work with our team to craft custom projects that meet your organization's business and upskilling goals.",
        plans: { individual: false, team: false, enterprise: '[Contact us](/get-started/enterprise)' },
      },
    ],
  },
  {
    title: 'Customer Success',
    rows: [
      {
        title: 'Learner and Program Manager onboarding',
        description:
          'Ensure smooth onboarding and initiation for learners and administrators. Available exclusively for annual plans.',
        plans: { individual: false, team: true, enterprise: true },
      },
      {
        title: 'Dedicated Customer Success Manager',
        description: 'Benefit from personalized guidance and support with a dedicated Customer Success Manager.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'Executive business reviews and impact reports',
        description: 'Understand and measure the ROI of Udacity for your business with quarterly impact reports.',
        plans: { individual: false, team: false, enterprise: true },
      },
    ],
  },
  {
    title: 'Integrations',
    rows: [
      {
        title: 'Single sign-on (SSO)',
        description: 'Enable business-wide single sign-on to enable easier and more secure access.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'API',
        description: 'Unlock select API including reporting, catalog, and others.',
        plans: { individual: false, team: true, enterprise: true },
      },
      {
        title: 'Basic Learning Management System (LMS) integration',
        description:
          'Unlock integrations with select LMS to ensure streamlined operations and seamless data synchronization.',
        plans: { individual: false, team: false, enterprise: true },
      },
      {
        title: 'Advanced Learning Management System (LMS) integration',
        description: 'Access custom LMS integrations for full compatibility and optimized learning workflows.',
        plans: { individual: false, team: false, enterprise: '[Contact us](/get-started/enterprise)' },
      },
    ],
  },
  {
    title: 'Billing',
    rows: [
      {
        title: 'Billing by PayPal or credit card',
        description: 'Purchase with Visa, Mastercard, American Express, or PayPal.',
        plans: { individual: true, team: true, enterprise: true },
      },
      {
        title: 'Billing by invoice',
        description: 'Enable enterprise ease with invoice payment options, including standard net 30 terms.',
        plans: { individual: false, team: true, enterprise: true },
      },
    ],
  },
];
